/**
 * VideoAnnotationCanvas Component
 *
 * Canvas-based annotation tool for drawing on videos.
 * Supports freehand drawing, shapes, arrows, and text.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { cn } from "@/lib/utils";

// Annotation Types
export type AnnotationTool =
  | "pen"
  | "highlighter"
  | "arrow"
  | "circle"
  | "rectangle"
  | "line"
  | "text"
  | "eraser";

export interface Point {
  x: number;
  y: number;
}

export interface Stroke {
  id: string;
  tool: AnnotationTool;
  points: Point[];
  color: string;
  strokeWidth: number;
  opacity: number;
}

export interface Shape {
  id: string;
  tool: "arrow" | "circle" | "rectangle" | "line";
  start: Point;
  end: Point;
  color: string;
  strokeWidth: number;
}

export interface TextLabel {
  id: string;
  position: Point;
  text: string;
  color: string;
  fontSize: number;
}

export interface AnnotationData {
  strokes: Stroke[];
  shapes: Shape[];
  textLabels: TextLabel[];
  version: number;
}

export interface VideoAnnotationCanvasProps {
  videoUrl?: string;
  width?: number;
  height?: number;
  currentTool?: AnnotationTool;
  currentColor?: string;
  strokeWidth?: number;
  initialAnnotations?: AnnotationData;
  readOnly?: boolean;
  showVideo?: boolean;
  className?: string;
  onAnnotationsChange?: (annotations: AnnotationData) => void;
}

export interface VideoAnnotationCanvasRef {
  clear: () => void;
  undo: () => void;
  redo: () => void;
  getAnnotations: () => AnnotationData;
  setAnnotations: (annotations: AnnotationData) => void;
  exportImage: () => string | null;
}

// Default empty annotations
const emptyAnnotations: AnnotationData = {
  strokes: [],
  shapes: [],
  textLabels: [],
  version: 1,
};

// Generate unique ID
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

export const VideoAnnotationCanvas = forwardRef<
  VideoAnnotationCanvasRef,
  VideoAnnotationCanvasProps
>(function VideoAnnotationCanvas(
  {
    videoUrl,
    width = 640,
    height = 360,
    currentTool = "pen",
    currentColor = "#ef4444",
    strokeWidth = 3,
    initialAnnotations,
    readOnly = false,
    showVideo = true,
    className,
    onAnnotationsChange,
  },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Annotation state
  const [annotations, setAnnotations] = useState<AnnotationData>(
    initialAnnotations || emptyAnnotations
  );
  const [history, setHistory] = useState<AnnotationData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
  const [currentShape, setCurrentShape] = useState<Shape | null>(null);
  const [startPoint, setStartPoint] = useState<Point | null>(null);

  // Text input state
  const [textInput, setTextInput] = useState<{
    position: Point;
    text: string;
  } | null>(null);

  // Push to history when annotations change
  const pushToHistory = useCallback((newAnnotations: AnnotationData) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      return [...newHistory, newAnnotations];
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  // Update annotations and notify parent
  const updateAnnotations = useCallback(
    (newAnnotations: AnnotationData) => {
      setAnnotations(newAnnotations);
      onAnnotationsChange?.(newAnnotations);
    },
    [onAnnotationsChange]
  );

  // Initialize from props
  useEffect(() => {
    if (initialAnnotations) {
      setAnnotations(initialAnnotations);
      setHistory([initialAnnotations]);
      setHistoryIndex(0);
    }
  }, [initialAnnotations]);

  // Draw everything to canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw strokes
    for (const stroke of annotations.strokes) {
      if (stroke.points.length < 2) continue;

      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = stroke.opacity;

      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Draw shapes
    for (const shape of annotations.shapes) {
      ctx.beginPath();
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = shape.strokeWidth;
      ctx.lineCap = "round";

      switch (shape.tool) {
        case "line":
          ctx.moveTo(shape.start.x, shape.start.y);
          ctx.lineTo(shape.end.x, shape.end.y);
          ctx.stroke();
          break;

        case "arrow":
          drawArrow(ctx, shape.start, shape.end, shape.color, shape.strokeWidth);
          break;

        case "circle":
          const radiusX = Math.abs(shape.end.x - shape.start.x) / 2;
          const radiusY = Math.abs(shape.end.y - shape.start.y) / 2;
          const centerX = shape.start.x + (shape.end.x - shape.start.x) / 2;
          const centerY = shape.start.y + (shape.end.y - shape.start.y) / 2;
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
          ctx.stroke();
          break;

        case "rectangle":
          const rectWidth = shape.end.x - shape.start.x;
          const rectHeight = shape.end.y - shape.start.y;
          ctx.strokeRect(shape.start.x, shape.start.y, rectWidth, rectHeight);
          break;
      }
    }

    // Draw text labels
    for (const label of annotations.textLabels) {
      ctx.font = `${label.fontSize}px sans-serif`;
      ctx.fillStyle = label.color;
      ctx.fillText(label.text, label.position.x, label.position.y);
    }

    // Draw current stroke (while drawing)
    if (currentStroke && currentStroke.points.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = currentStroke.color;
      ctx.lineWidth = currentStroke.strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalAlpha = currentStroke.opacity;

      ctx.moveTo(currentStroke.points[0].x, currentStroke.points[0].y);
      for (let i = 1; i < currentStroke.points.length; i++) {
        ctx.lineTo(currentStroke.points[i].x, currentStroke.points[i].y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Draw current shape (while drawing)
    if (currentShape && startPoint) {
      ctx.beginPath();
      ctx.strokeStyle = currentShape.color;
      ctx.lineWidth = currentShape.strokeWidth;
      ctx.setLineDash([5, 5]);

      switch (currentShape.tool) {
        case "line":
          ctx.moveTo(currentShape.start.x, currentShape.start.y);
          ctx.lineTo(currentShape.end.x, currentShape.end.y);
          ctx.stroke();
          break;

        case "arrow":
          drawArrow(
            ctx,
            currentShape.start,
            currentShape.end,
            currentShape.color,
            currentShape.strokeWidth
          );
          break;

        case "circle":
          const radiusX = Math.abs(currentShape.end.x - currentShape.start.x) / 2;
          const radiusY = Math.abs(currentShape.end.y - currentShape.start.y) / 2;
          const centerX =
            currentShape.start.x + (currentShape.end.x - currentShape.start.x) / 2;
          const centerY =
            currentShape.start.y + (currentShape.end.y - currentShape.start.y) / 2;
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
          ctx.stroke();
          break;

        case "rectangle":
          const rectWidth = currentShape.end.x - currentShape.start.x;
          const rectHeight = currentShape.end.y - currentShape.start.y;
          ctx.strokeRect(
            currentShape.start.x,
            currentShape.start.y,
            rectWidth,
            rectHeight
          );
          break;
      }
      ctx.setLineDash([]);
    }
  }, [annotations, currentStroke, currentShape, startPoint]);

  // Redraw when annotations change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Draw arrow helper
  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    color: string,
    lineWidth: number
  ) => {
    const headLength = 15;
    const angle = Math.atan2(end.y - start.y, end.x - start.x);

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // Arrow head
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLength * Math.cos(angle - Math.PI / 6),
      end.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      end.x - headLength * Math.cos(angle + Math.PI / 6),
      end.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();
  };

  // Get point from event
  const getPoint = (e: React.MouseEvent | React.TouchEvent): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number, clientY: number;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Handle drawing start
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (readOnly) return;
    e.preventDefault();

    const point = getPoint(e);
    if (!point) return;

    setIsDrawing(true);
    setStartPoint(point);

    if (currentTool === "pen" || currentTool === "highlighter" || currentTool === "eraser") {
      setCurrentStroke({
        id: generateId(),
        tool: currentTool,
        points: [point],
        color: currentTool === "eraser" ? "#ffffff" : currentColor,
        strokeWidth: currentTool === "highlighter" ? strokeWidth * 3 : strokeWidth,
        opacity: currentTool === "highlighter" ? 0.4 : 1,
      });
    } else if (["arrow", "circle", "rectangle", "line"].includes(currentTool)) {
      setCurrentShape({
        id: generateId(),
        tool: currentTool as "arrow" | "circle" | "rectangle" | "line",
        start: point,
        end: point,
        color: currentColor,
        strokeWidth,
      });
    } else if (currentTool === "text") {
      setTextInput({ position: point, text: "" });
    }
  };

  // Handle drawing move
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();

    const point = getPoint(e);
    if (!point) return;

    if (currentStroke) {
      setCurrentStroke((prev) =>
        prev ? { ...prev, points: [...prev.points, point] } : null
      );
    } else if (currentShape) {
      setCurrentShape((prev) => (prev ? { ...prev, end: point } : null));
    }
  };

  // Handle drawing end
  const handleEnd = () => {
    if (!isDrawing || readOnly) return;
    setIsDrawing(false);

    if (currentStroke && currentStroke.points.length > 1) {
      const newAnnotations = {
        ...annotations,
        strokes: [...annotations.strokes, currentStroke],
      };
      updateAnnotations(newAnnotations);
      pushToHistory(newAnnotations);
    }

    if (currentShape) {
      const newAnnotations = {
        ...annotations,
        shapes: [...annotations.shapes, currentShape],
      };
      updateAnnotations(newAnnotations);
      pushToHistory(newAnnotations);
    }

    setCurrentStroke(null);
    setCurrentShape(null);
    setStartPoint(null);
  };

  // Handle text input submit
  const handleTextSubmit = () => {
    if (!textInput || !textInput.text.trim()) {
      setTextInput(null);
      return;
    }

    const newLabel: TextLabel = {
      id: generateId(),
      position: textInput.position,
      text: textInput.text.trim(),
      color: currentColor,
      fontSize: 16,
    };

    const newAnnotations = {
      ...annotations,
      textLabels: [...annotations.textLabels, newLabel],
    };
    updateAnnotations(newAnnotations);
    pushToHistory(newAnnotations);
    setTextInput(null);
  };

  // Exposed methods
  useImperativeHandle(ref, () => ({
    clear: () => {
      const newAnnotations = { ...emptyAnnotations };
      updateAnnotations(newAnnotations);
      pushToHistory(newAnnotations);
    },
    undo: () => {
      if (historyIndex > 0) {
        setHistoryIndex((prev) => prev - 1);
        updateAnnotations(history[historyIndex - 1]);
      }
    },
    redo: () => {
      if (historyIndex < history.length - 1) {
        setHistoryIndex((prev) => prev + 1);
        updateAnnotations(history[historyIndex + 1]);
      }
    },
    getAnnotations: () => annotations,
    setAnnotations: (newAnnotations: AnnotationData) => {
      updateAnnotations(newAnnotations);
      pushToHistory(newAnnotations);
    },
    exportImage: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      return canvas.toDataURL("image/png");
    },
  }));

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      style={{ width, height }}
    >
      {/* Video layer */}
      {showVideo && videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          controls
        />
      )}

      {/* Canvas layer */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={cn(
          "absolute inset-0 w-full h-full",
          readOnly ? "pointer-events-none" : "cursor-crosshair",
          showVideo && videoUrl && "z-10"
        )}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />

      {/* Text input overlay */}
      {textInput && (
        <div
          className="absolute z-20 flex items-center gap-1"
          style={{
            left: textInput.position.x,
            top: textInput.position.y,
            transform: "translate(-8px, -12px)",
          }}
        >
          <input
            type="text"
            value={textInput.text}
            onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleTextSubmit();
              } else if (e.key === "Escape") {
                setTextInput(null);
              }
            }}
            onBlur={handleTextSubmit}
            autoFocus
            className="px-2 py-1 text-sm border rounded shadow-md bg-background"
            placeholder="Enter text..."
          />
        </div>
      )}
    </div>
  );
});

export default VideoAnnotationCanvas;
